// src/components/AppLayout.tsx
// Responsive layout:
// Desktop (≥768px): fixed sidebar on the left
// Mobile (<768px): bottom tab bar + slide-in drawer for secondary nav

import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Primary nav — shown in bottom tab bar on mobile
const PRIMARY_NAV = [
  { to: '/dashboard',         label: 'Today',    icon: '☀️' },
  { to: '/week',              label: 'Week',     icon: '📅' },
  { to: '/summary',           label: 'Summary',  icon: '📊' },
  { to: '/family-fund',       label: 'Fund',     icon: '💰' },
];

// Secondary nav — shown in sidebar on desktop, in drawer on mobile
const SECONDARY_NAV = [
  { to: '/habits/config',     label: 'Habits',        icon: '🎯' },
  { to: '/habits/priorities', label: 'Priorities',    icon: '⭐' },
  { to: '/win-log',           label: 'Win Log',       icon: '🏆' },
  { to: '/reflection',        label: 'Reflection',    icon: '💬' },
  { to: '/settings',          label: 'Settings',      icon: '⚙️' },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const location         = useLocation();
  const isMobile         = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    navigate('/login');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>

      {/* ══════════════ DESKTOP SIDEBAR ══════════════ */}
      {!isMobile && (
        <aside style={{
          width: 'var(--sidebar-width)',
          background: 'var(--bg2)',
          borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column',
          position: 'fixed', top: 0, left: 0, height: '100vh',
          overflowY: 'auto', zIndex: 10,
        }}>
          <SidebarContent user={user} onLogout={handleLogout} loggingOut={loggingOut} />
        </aside>
      )}

      {/* ══════════════ MOBILE HEADER ══════════════ */}
      {isMobile && (
        <header style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 20,
          background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', height: 52,
        }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 500, color: 'var(--green)' }}>
            habit<span style={{ color: 'var(--text3)' }}>.</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{user?.name?.split(' ')[0] || user?.username}</span>
            <button onClick={() => setDrawerOpen(true)} style={{
              width: 40, height: 40, borderRadius: 'var(--radius-sm)',
              background: 'transparent', border: '1px solid var(--border)',
              color: 'var(--text2)', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              ☰
            </button>
          </div>
        </header>
      )}

      {/* ══════════════ MOBILE DRAWER ══════════════ */}
      {isMobile && (
        <>
          {/* Backdrop */}
          {drawerOpen && (
            <div onClick={() => setDrawerOpen(false)} style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(2px)', zIndex: 30,
            }} />
          )}

          {/* Drawer */}
          <aside style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 40,
            width: 260, background: 'var(--bg2)',
            borderLeft: '1px solid var(--border)',
            transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.25s ease',
            display: 'flex', flexDirection: 'column',
            overflowY: 'auto',
          }}>
            {/* Drawer header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: '1px solid var(--border)',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 16, color: 'var(--green)' }}>
                habit<span style={{ color: 'var(--text3)' }}>.</span>
              </div>
              <button onClick={() => setDrawerOpen(false)} style={{
                width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                background: 'transparent', border: '1px solid var(--border)',
                color: 'var(--text3)', fontSize: 18, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                ✕
              </button>
            </div>

            {/* Drawer nav */}
            <nav style={{ padding: '12px 10px', flex: 1 }}>
              <DrawerSection label="Track" />
              {PRIMARY_NAV.map(item => <DrawerLink key={item.to} {...item} />)}

              <DrawerSection label="Manage" />
              {SECONDARY_NAV.map(item => <DrawerLink key={item.to} {...item} />)}

              {user?.role === 'admin' && (
                <>
                  <DrawerSection label="Admin" />
                  <DrawerLink to="/admin/users" label="Family Members" icon="👥" />
                </>
              )}
            </nav>

            {/* Logout */}
            <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
              <button onClick={handleLogout} disabled={loggingOut} style={{
                width: '100%', padding: '12px',
                background: 'transparent', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', color: 'var(--text3)',
                fontSize: 13, cursor: 'pointer',
              }}>
                {loggingOut ? 'logging out...' : 'Log out'}
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ══════════════ MAIN CONTENT ══════════════ */}
      <main style={{
        marginLeft: isMobile ? 0 : 'var(--sidebar-width)',
        flex: 1,
        padding: isMobile ? '68px 16px 80px' : '32px 36px',
        maxWidth: isMobile ? '100vw' : 'calc(100vw - var(--sidebar-width))',
        minHeight: '100vh',
      }}>
        <Outlet />
      </main>

      {/* ══════════════ MOBILE BOTTOM TAB BAR ══════════════ */}
      {isMobile && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 20,
          background: 'var(--bg2)', borderTop: '1px solid var(--border)',
          display: 'flex', height: 'var(--bottom-nav-height)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}>
          {PRIMARY_NAV.map(item => (
            <BottomTabItem key={item.to} {...item} />
          ))}
          {/* More button opens drawer */}
          <button onClick={() => setDrawerOpen(true)} style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 3,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text3)', padding: '6px 4px',
          }}>
            <span style={{ fontSize: 18 }}>⋯</span>
            <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)' }}>More</span>
          </button>
        </nav>
      )}
    </div>
  );
}

// ── DESKTOP SIDEBAR CONTENT ──────────────────────────────────────

function SidebarContent({ user, onLogout, loggingOut }: {
  user: any; onLogout: () => void; loggingOut: boolean;
}) {
  return (
    <>
      <div style={{ padding: '24px 20px 18px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 500, color: 'var(--green)', letterSpacing: -0.5 }}>
          habit<span style={{ color: 'var(--text3)' }}>.</span>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6 }}>
          {user?.name || user?.username}
        </div>
      </div>

      <nav style={{ padding: '14px 10px', flex: 1 }}>
        <NavSection label="Track" />
        {PRIMARY_NAV.map(item => <SidebarLink key={item.to} {...item} />)}
        <NavSection label="Manage" />
        {SECONDARY_NAV.map(item => <SidebarLink key={item.to} {...item} />)}
        {user?.role === 'admin' && (
          <>
            <NavSection label="Admin" />
            <SidebarLink to="/admin/users" label="Family Members" icon="👥" />
          </>
        )}
      </nav>

      <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
        <button onClick={onLogout} disabled={loggingOut} style={{
          width: '100%', padding: '8px 12px',
          background: 'transparent', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', color: 'var(--text3)',
          fontSize: 11, cursor: 'pointer', transition: 'all 0.15s',
        }}>
          {loggingOut ? 'logging out...' : 'Log out'}
        </button>
      </div>
    </>
  );
}

// ── SHARED COMPONENTS ─────────────────────────────────────────────

function NavSection({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '1.2px',
      textTransform: 'uppercase', color: 'var(--text3)',
      fontFamily: 'var(--font-mono)', padding: '10px 10px 5px',
    }}>
      {label}
    </div>
  );
}

function DrawerSection({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '1.2px',
      textTransform: 'uppercase', color: 'var(--text3)',
      fontFamily: 'var(--font-mono)', padding: '12px 10px 6px',
    }}>
      {label}
    </div>
  );
}

function SidebarLink({ to, label, icon }: { to: string; label: string; icon: string }) {
  return (
    <NavLink to={to} style={({ isActive }) => ({
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px', borderRadius: 8,
      fontSize: 12, marginBottom: 2, transition: 'all 0.15s',
      color: isActive ? 'var(--green)' : 'var(--text2)',
      background: isActive ? 'var(--green-dim)' : 'transparent',
      border: isActive ? '1px solid rgba(0,229,160,0.2)' : '1px solid transparent',
      textDecoration: 'none',
    })}>
      <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>{icon}</span>
      {label}
    </NavLink>
  );
}

function DrawerLink({ to, label, icon }: { to: string; label: string; icon: string }) {
  return (
    <NavLink to={to} style={({ isActive }) => ({
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 10px', borderRadius: 8, marginBottom: 2,
      fontSize: 14, transition: 'all 0.15s', minHeight: 44,
      color: isActive ? 'var(--green)' : 'var(--text2)',
      background: isActive ? 'var(--green-dim)' : 'transparent',
      border: isActive ? '1px solid rgba(0,229,160,0.2)' : '1px solid transparent',
      textDecoration: 'none',
    })}>
      <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{icon}</span>
      {label}
    </NavLink>
  );
}

function BottomTabItem({ to, label, icon }: { to: string; label: string; icon: string }) {
  return (
    <NavLink to={to} style={({ isActive }) => ({
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 3,
      color: isActive ? 'var(--green)' : 'var(--text3)',
      textDecoration: 'none', padding: '6px 4px',
      borderTop: isActive ? '2px solid var(--green)' : '2px solid transparent',
      transition: 'all 0.15s',
    })}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)' }}>{label}</span>
    </NavLink>
  );
}
